import { useEffect, useRef, useState } from 'react'
import { api, upload } from '../api/client.ts'
import { useAuth } from '../store/auth.ts'
import { addPasskey, canUsePasskeys } from '../lib/passkeys.ts'
import { Avatar } from './Avatar.tsx'
import { PasswordInput } from './PasswordInput.tsx'

type Profile = {
  id: string
  email: string | null
  displayName: string
  avatarUrl: string | null
  birthday: string | null
  timezone: string
  isAdmin: boolean
  /** Спрашивается ли при входе одноразовый код */
  totpEnabled?: boolean
}

/** Название пояса в понятном виде: «Москва, +03:00». */
function timezoneLabel(zone: string): string {
  const city = zone.split('/')[1]?.replace(/_/g, ' ') ?? zone
  try {
    const parts = new Intl.DateTimeFormat('ru-RU', {
      timeZone: zone,
      timeZoneName: 'shortOffset',
    }).formatToParts(new Date())
    const offset = parts.find((p) => p.type === 'timeZoneName')?.value ?? ''
    return `${city}, ${offset.replace('GMT', 'UTC')}`
  } catch {
    return city
  }
}

type PasskeyRow = { id: string; label: string; createdAt: string; lastUsedAt: string | null }

/** «19 сентября» — для строчки «последний раз». */
function dateOnly(iso: string): string {
  try {
    return new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long' }).format(new Date(iso))
  } catch {
    return 'давно'
  }
}

/**
 * Как назвать заводимый ключ.
 *
 * Человек заводит его на том устройстве, с которого сейчас смотрит, —
 * значит, название должно отвечать на вопрос «а это какое из моих?».
 */
function deviceName(): string {
  const ua = navigator.userAgent
  if (/iPhone/.test(ua)) return 'iPhone'
  if (/iPad/.test(ua)) return 'iPad'
  if (/Android/.test(ua)) return 'Телефон на Android'
  if (/Macintosh/.test(ua)) return 'Mac'
  if (/Windows/.test(ua)) return 'Компьютер'
  return 'Это устройство'
}

export function ProfileSettings({ onClose }: { onClose: () => void }) {
  const setUser = useAuth((s) => s.setUser)
  const login = useAuth((s) => s.login)
  const logout = useAuth((s) => s.logout)

  const [profile, setProfile] = useState<Profile | null>(null)
  const [timezones, setTimezones] = useState<string[]>([])
  const [name, setName] = useState('')
  const [birthday, setBirthday] = useState('')
  const [timezone, setTimezone] = useState('')

  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /** Смена пароля: открыта ли форма, что в ней и что пошло не так */
  const [changing, setChanging] = useState(false)
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [passError, setPassError] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    api<{ profile: Profile; timezones: string[] }>('/api/profile')
      .then((data) => {
        setProfile(data.profile)
        setTimezones(data.timezones)
        setName(data.profile.displayName)
        setBirthday(data.profile.birthday ?? '')
        setTimezone(data.profile.timezone)
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Не удалось загрузить'))
  }, [])

  const apply = (updated: Profile) => {
    setProfile(updated)
    // Обновляем и то, что видно в шапке: иначе имя поменяется
    // только после следующего входа
    setUser({
      id: updated.id,
      email: updated.email,
      displayName: updated.displayName,
      avatarUrl: updated.avatarUrl,
      timezone: updated.timezone,
      isAdmin: updated.isAdmin,
    })
  }

  /* Второй ключ при входе -------------------------------------------------- */

  const [totpSecret, setTotpSecret] = useState('')
  const [totpCode, setTotpCode] = useState('')
  const [totpError, setTotpError] = useState<string | null>(null)

  const startTotp = async () => {
    setBusy(true)
    setTotpError(null)
    try {
      const made = await api<{ secret: string }>('/api/profile/totp/start', { method: 'POST' })
      setTotpSecret(made.secret)
    } catch (failure) {
      setTotpError(failure instanceof Error ? failure.message : 'Не вышло начать')
    } finally {
      setBusy(false)
    }
  }

  const enableTotp = async () => {
    setBusy(true)
    setTotpError(null)
    try {
      await api('/api/profile/totp/enable', { method: 'POST', body: { code: totpCode } })
      setTotpSecret('')
      setTotpCode('')
      // Перечитываем: признак «включено» живёт в профиле
      const fresh = await api<{ profile: Profile }>('/api/profile')
      setProfile(fresh.profile)
    } catch (failure) {
      setTotpError(failure instanceof Error ? failure.message : 'Код не подошёл')
    } finally {
      setBusy(false)
    }
  }

  /**
   * Выключение спрашивает пароль, а не код.
   *
   * Телефон с кодами мог потеряться — тогда выключить его же кодом
   * невозможно.
   */
  const disableTotp = async () => {
    const password = window.prompt('Пароль, чтобы выключить второй ключ:')
    if (!password) return

    setBusy(true)
    setTotpError(null)
    try {
      await api('/api/profile/totp', { method: 'DELETE', body: { password } })
      const fresh = await api<{ profile: Profile }>('/api/profile')
      setProfile(fresh.profile)
    } catch (failure) {
      setTotpError(failure instanceof Error ? failure.message : 'Не вышло выключить')
    } finally {
      setBusy(false)
    }
  }

  /* Ключи этого человека -------------------------------------------------- */

  const [passkeys, setPasskeys] = useState<PasskeyRow[]>([])
  const [canPasskey, setCanPasskey] = useState(false)
  const [keyError, setKeyError] = useState<string | null>(null)

  useEffect(() => {
    void canUsePasskeys().then(setCanPasskey)
    void api<{ passkeys: PasskeyRow[] }>('/api/passkeys')
      .then((data) => setPasskeys(data.passkeys))
      .catch(() => {
        /* список ключей — не то, ради чего стоит рушить настройки */
      })
  }, [])

  const addKey = async () => {
    setBusy(true)
    setKeyError(null)
    try {
      // Название по умолчанию — то, что человек и так знает про своё
      // устройство: «этот телефон». Переименовать можно будет потом
      await addPasskey(deviceName())
      const data = await api<{ passkeys: PasskeyRow[] }>('/api/passkeys')
      setPasskeys(data.passkeys)
    } catch (failure) {
      const name = (failure as { name?: string }).name
      // Передумал или приложил не тот палец — это не поломка
      if (name !== 'NotAllowedError' && name !== 'AbortError') {
        setKeyError(failure instanceof Error ? failure.message : 'Не вышло добавить ключ')
      }
    } finally {
      setBusy(false)
    }
  }

  const removeKey = async (id: string) => {
    if (!window.confirm('Убрать этот ключ? Входить с него больше не получится.')) return

    setBusy(true)
    try {
      await api(`/api/passkeys/${id}`, { method: 'DELETE' })
      setPasskeys((list) => list.filter((k) => k.id !== id))
    } catch (failure) {
      setKeyError(failure instanceof Error ? failure.message : 'Не вышло убрать ключ')
    } finally {
      setBusy(false)
    }
  }

  /**
   * Сменить пароль.
   *
   * Сервер обрывает все входы, включая этот: после смены пароля
   * в доме не должно остаться ни одного старого ключа. Поэтому сразу
   * входим заново — пароль у нас в руках, человек только что его набрал.
   */
  const changePassword = async () => {
    setBusy(true)
    setPassError(null)
    try {
      await api('/api/profile/password', {
        method: 'PATCH',
        body: { current, next },
      })

      /**
       * Входим заново — но не всегда это получается в одно действие.
       *
       * Если у человека включён второй ключ, сервер после пароля
       * отдаёт не доступ, а пропуск под код. Молча проглотить его
       * нельзя: приложение осталось бы без ключей и через минуту
       * само выкинуло бы человека на вход, будто он что-то сломал.
       */
      const needCode = await login(profile?.email ?? '', next)

      setChanging(false)
      setCurrent('')
      setNext('')

      if (needCode) {
        window.alert('Пароль сменён. Войди заново — понадобится код из приложения.')
        logout()
        return
      }

      setSaved(true)
    } catch (failure) {
      setPassError(failure instanceof Error ? failure.message : 'Не вышло сменить пароль')
    } finally {
      setBusy(false)
    }
  }

  const save = async () => {
    setBusy(true)
    setError(null)
    setSaved(false)
    try {
      const data = await api<{ profile: Profile }>('/api/profile', {
        method: 'PATCH',
        body: {
          displayName: name.trim(),
          birthday: birthday || null,
          timezone,
        },
      })
      apply(data.profile)
      setSaved(true)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не получилось сохранить')
    } finally {
      setBusy(false)
    }
  }

  const uploadAvatar = async (file: File) => {
    setBusy(true)
    setError(null)
    try {
      const form = new FormData()
      form.append('avatar', file, file.name)
      const data = await upload<{ profile: Profile }>('/api/profile/avatar', form)
      apply(data.profile)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить фото')
    } finally {
      setBusy(false)
    }
  }

  const removeAvatar = async () => {
    setBusy(true)
    try {
      const data = await api<{ profile: Profile }>('/api/profile/avatar', { method: 'DELETE' })
      apply(data.profile)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не получилось')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="sheet" onClick={onClose}>
      <div className="sheet-body" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-head">
          <h2>Профиль</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Закрыть">
            ✕
          </button>
        </div>

        {!profile && !error && (
          <div className="center-screen" style={{ height: 120 }}>
            <div className="spinner" />
          </div>
        )}

        {profile && (
          <>
            <div className="profile-avatar">
              <Avatar
                id={profile.id}
                name={profile.displayName}
                url={profile.avatarUrl}
                size={72}
              />
              <div className="profile-avatar-actions">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) void uploadAvatar(file)
                    e.target.value = ''
                  }}
                />
                <button
                  className="btn-quiet"
                  onClick={() => fileRef.current?.click()}
                  disabled={busy}
                >
                  {profile.avatarUrl ? 'Заменить фото' : 'Загрузить фото'}
                </button>
                {profile.avatarUrl && (
                  <button className="link-btn" onClick={() => void removeAvatar()} disabled={busy}>
                    Убрать
                  </button>
                )}
              </div>
            </div>

            <label className="field">
              <span className="field-label">Имя</span>
              <input
                className="input"
                value={name}
                onChange={(e) => {
                  setName(e.target.value)
                  setSaved(false)
                }}
                maxLength={64}
              />
            </label>

            <label className="field">
              <span className="field-label">День рождения</span>
              <input
                className="input"
                type="date"
                value={birthday}
                onChange={(e) => {
                  setBirthday(e.target.value)
                  setSaved(false)
                }}
              />
              <span className="field-hint">
                Появится в календаре у всей семьи — напомнит без твоего участия
              </span>
            </label>

            <label className="field">
              <span className="field-label">Часовой пояс</span>
              <select
                className="input"
                value={timezone}
                onChange={(e) => {
                  setTimezone(e.target.value)
                  setSaved(false)
                }}
              >
                {!timezones.includes(timezone) && (
                  <option value={timezone}>{timezoneLabel(timezone)}</option>
                )}
                {timezones.map((zone) => (
                  <option key={zone} value={zone}>
                    {timezoneLabel(zone)}
                  </option>
                ))}
              </select>
              <span className="field-hint">
                От него зависит, в какое время ты увидишь события и напоминания
              </span>
            </label>

            {error && <div className="sheet-error">{error}</div>}
            {saved && <div className="sheet-ok">Сохранено</div>}

            <button className="btn-primary" onClick={() => void save()} disabled={busy || !name.trim()}>
              {busy ? '…' : 'Сохранить'}
            </button>

            <p className="sheet-note">{profile.email}</p>

            {/**
             * СМЕНА ПАРОЛЯ
             *
             * До сих пор пароль нельзя было сменить вовсе. Выяснилось
             * это не из списка возможностей, а когда в семье первый раз
             * понадобилось — и человек остался снаружи.
             */}
            {/**
             * ВТОРОЙ КЛЮЧ ПРИ ВХОДЕ
             *
             * Заводится в два шага: сначала показываем тайну, потом
             * просим подтвердить кодом. Иначе человек включит её,
             * не успеет настроить телефон — и запрёт себя снаружи.
             */}
            <h3 className="sheet-sub">Второй ключ при входе</h3>

            {profile.totpEnabled ? (
              <>
                <p className="sheet-text">
                  При входе спрашивается одноразовый код из приложения-аутентификатора.
                </p>
                <button className="btn-quiet" onClick={() => void disableTotp()} disabled={busy}>
                  Выключить
                </button>
              </>
            ) : totpSecret ? (
              <>
                <p className="sheet-text">
                  Добавьте эту тайну в приложение-аутентификатор, а потом введите
                  код, который оно покажет.
                </p>
                <div className="totp-secret">{totpSecret}</div>
                <label className="field">
                  <span className="field-label">Код из приложения</span>
                  <input
                    className="input input-code"
                    inputMode="numeric"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                  />
                </label>
                <button
                  className="btn-primary"
                  onClick={() => void enableTotp()}
                  disabled={busy || totpCode.length !== 6}
                >
                  Включить
                </button>
              </>
            ) : (
              <button className="btn-quiet" onClick={() => void startTotp()} disabled={busy}>
                Включить второй ключ
              </button>
            )}

            {totpError && <div className="sheet-error">{totpError}</div>}

            {/**
             * КЛЮЧИ ЭТОГО ЧЕЛОВЕКА
             *
             * Телефон хранит ключ у себя и отдаёт только хозяину — по
             * лицу, отпечатку или коду разблокировки. Пароль при этом
             * остаётся: телефон можно разбить, и войти тогда надо
             * чем-то ещё.
             */}
            <h3 className="sheet-sub">Вход лицом</h3>

            {passkeys.length > 0 && (
              <div className="key-list">
                {passkeys.map((key) => (
                  <div key={key.id} className="key-row">
                    <div className="key-text">
                      <div className="key-name">{key.label}</div>
                      <div className="key-sub">
                        {key.lastUsedAt ? 'последний раз ' + dateOnly(key.lastUsedAt) : 'ещё не использовался'}
                      </div>
                    </div>
                    <button
                      className="link-btn is-danger"
                      onClick={() => void removeKey(key.id)}
                      disabled={busy}
                    >
                      убрать
                    </button>
                  </div>
                ))}
              </div>
            )}

            {canPasskey ? (
              <button className="btn-quiet" onClick={() => void addKey()} disabled={busy}>
                {busy ? '…' : 'Добавить это устройство'}
              </button>
            ) : (
              <p className="sheet-note">
                Это устройство не умеет хранить ключ у себя — вход только паролем
              </p>
            )}

            {keyError && <div className="sheet-error">{keyError}</div>}

            <h3 className="sheet-sub">Пароль</h3>

            {!changing ? (
              <button className="btn-quiet" onClick={() => setChanging(true)}>
                Сменить пароль
              </button>
            ) : (
              <>
                <label className="field">
                  <span className="field-label">Нынешний пароль</span>
                  <PasswordInput
                    className="input"
                    autoComplete="current-password"
                    value={current}
                    onChange={(e) => {
                      setCurrent(e.target.value)
                      setPassError(null)
                    }}
                  />
                </label>

                <label className="field">
                  <span className="field-label">Новый пароль</span>
                  <PasswordInput
                    className="input"
                    autoComplete="new-password"
                    value={next}
                    onChange={(e) => {
                      setNext(e.target.value)
                      setPassError(null)
                    }}
                  />
                  <span className="field-hint">
                    Не короче восьми знаков. После смены другие устройства
                    попросят войти заново
                  </span>
                </label>

                {passError && <div className="sheet-error">{passError}</div>}

                <button
                  className="btn-primary"
                  onClick={() => void changePassword()}
                  disabled={busy || current.length === 0 || next.length < 8}
                >
                  {busy ? '…' : 'Сменить'}
                </button>
                <button
                  className="btn-quiet"
                  onClick={() => {
                    setChanging(false)
                    setCurrent('')
                    setNext('')
                    setPassError(null)
                  }}
                >
                  Отмена
                </button>
              </>
            )}
          </>
        )}

        {!profile && error && <div className="sheet-error">{error}</div>}
      </div>
    </div>
  )
}
